import { postQuery } from "../api";

/**
 * Subscribes a user to email or SMS notifications for garbage day reminders.
 *
 * @async
 * @function
 * @param {'email'|'text'} type - The type of subscription, either 'email' or 'sms'.
 * @param {string} userInfo - The user's email address (if type is 'email') or phone number (if type is 'sms').
 * @param {string} garbageDay - The day of the week for garbage collection.
 * @returns {Promise<any>} The response from the subscription API.
 * @throws Will throw an error if the API request fails.
 */
export const subscribeEmailOrSms = async (type, emailOrPhone, garbageDay) => {
  const url = type === "email" ? process.env.REACT_APP_SUBSCRIBE_EMAIL_URL : process.env.REACT_APP_SUBSCRIBE_SMS_URL;
  const body = type === "email" ? { email: emailOrPhone, garbageDay } : { destinationNumber: emailOrPhone, garbageDay };

  try {
    const response = await postQuery(url, body);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Subscribes a device to garbage collection push notifications.
 *
 * @async
 * @function
 * @param {string} pushToken - The FCM push notification token.
 * @param {string} garbageDay - The day of the week for garbage collection.
 * @returns {Promise<any>} The response from the subscription API.
 * @throws Will throw an error if the API request fails.
 */
export const subscribeGarbagePush = async (pushToken, garbageDay) => {
  const response = await postQuery(process.env.REACT_APP_PUSH_NOTIFICATIONS, {
    pushToken,
    garbageDay,
    token: "empty",
  });
  return response;
};

/**
 * Unsubscribes a device from garbage collection push notifications.
 *
 * @async
 * @function
 * @param {string} pushToken - The FCM push notification token.
 * @returns {Promise<any>} The response from the subscription API.
 * @throws Will throw an error if the API request fails.
 */
export const unsubscribeGarbagePush = async (pushToken) => {
  const response = await postQuery(process.env.REACT_APP_UNSUBSCRIBE_PUSH, {
    pushToken,
  });
  return response;
};

/**
 * Subscribes a device token to web push notifications for a given topic.
 * Appends the environment suffix to the topic for non-prod environments.
 *
 * @async
 * @function
 * @param {string} topic - The base topic name (e.g. "news_releases").
 * @param {string} token - The FCM registration token.
 * @returns {Promise<{success: boolean}|null>} The subscription response, or null on failure.
 */
export const subscribeToWebPushNotifications = async (topic, token) => {
  let channel = topic;
  if (process.env.REACT_APP_ENV !== "prod") {
    channel = `${channel}-${process.env.REACT_APP_ENV}`;
  }
  const response = await postQuery(`${process.env.REACT_APP_API_URL}/subscribe-push`, {
    token,
    topic: channel,
  });
  return response;
};

/**
 * Unsubscribes a device token from web push notifications for a given topic.
 * Appends the environment suffix to the topic for non-prod environments.
 *
 * @async
 * @function
 * @param {string} topic - The base topic name (e.g. "news_releases").
 * @param {string} token - The FCM registration token.
 * @returns {Promise<{success: boolean}|null>} The unsubscription response, or null on failure.
 */
export const unsubscribeFromWebPushNotifications = async (topic, token) => {
  let channel = topic;
  if (process.env.REACT_APP_ENV !== "prod") {
    channel = `${channel}-${process.env.REACT_APP_ENV}`;
  }
  const response = await postQuery(`${process.env.REACT_APP_API_URL}/unsubscribe-push`, {
    token,
    topic: channel,
  });
  return response;
};
